import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '~/components/shell/useMediaQuery';
import { createSilkRenderer, SilkRenderer } from './silkRenderer';
import styles from './SilkBackground.module.scss';

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

type IdleHandle = { cancel: () => void };

/** Start płótna dopiero po narysowaniu formularza — logowanie nie czeka na grafikę. */
function whenIdle(callback: () => void): IdleHandle {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, { timeout: 500 });

    return { cancel: () => window.cancelIdleCallback(id) };
  }
  const id = window.setTimeout(callback, 120);

  return { cancel: () => window.clearTimeout(id) };
}

/**
 * Tło ekranów /auth: jedwabne wstęgi WebGL za treścią. Bez WebGL, po utracie kontekstu i do czasu
 * startu płótna widać rozmyty pas CSS w tych samych barwach. Przy ograniczonym ruchu i w ukrytej
 * karcie przeglądarki wstęgi stoją (jedna nieruchoma klatka).
 */
const SilkBackground: FunctionComponent = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<SilkRenderer | null>(null);
  const [webgl, setWebgl] = useState(false);
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let observer: ResizeObserver | undefined;
    const idle = whenIdle(() => {
      const renderer = createSilkRenderer(canvas, () => {
        rendererRef.current = null;
        setWebgl(false);
      });
      if (!renderer) return;
      rendererRef.current = renderer;
      setWebgl(true);
      if (typeof ResizeObserver === 'function') {
        observer = new ResizeObserver(() => renderer.resize());
        observer.observe(canvas);
      }
    });

    return () => {
      idle.cancel();
      observer?.disconnect();
      rendererRef.current?.destroy();
      rendererRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!webgl) return undefined;
    const apply = () =>
      rendererRef.current?.setRunning(!reducedMotion && document.visibilityState === 'visible');
    apply();
    document.addEventListener('visibilitychange', apply);

    return () => {
      document.removeEventListener('visibilitychange', apply);
      rendererRef.current?.setRunning(false);
    };
  }, [webgl, reducedMotion]);

  return (
    <div className={`${styles.silk} ${webgl ? styles.webgl : ''}`} aria-hidden="true">
      <canvas ref={canvasRef} className={styles.canvas} />
    </div>
  );
};

export default SilkBackground;
